<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import feedbackSourceService from "@/services/feedbackSource.service"
import FeedbackSourceDrawer from '@/views/feedback/master/FeedbackSourceDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 500)
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'master-source',
    subject: 'feedback.menu',
    action: 'master_source',
  },
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Source Type',
    key: 'source_type',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const sourceType = ref()
const sourceTypes = await getEnumValue('SourceTypes', true)

const {
  data: feedbackSourcesData,
  execute: fetchFeedbackSources,
  isFetching: isFetchingSources,
} = await useApi(createUrl('/masters/feedback-sources', {
  query: {
    paged: 1,
    q: debouncedSearch,
    search: {
      source_type: sourceType,
    },
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const feedbackSources = computed(() => feedbackSourcesData.value?.data)
const totalFeedbackSources = computed(() => feedbackSourcesData.value?.meta?.total)

const updateFeedbackSource = data => {
  if (data.id){
    feedbackSourceService.updateFeedbackSource(data.id, data, () => {
      fetchFeedbackSources()
      isFeedbackSourceDrawerVisible.value = false
      resetErrors()
    }, onError)
  }
}

const saveFeedbackSource = data => {
  feedbackSourceService.addFeedbackSource(data, () => {
    fetchFeedbackSources()
    isFeedbackSourceDrawerVisible.value = false
    resetErrors()
  }, onError)
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const isFeedbackSourceDrawerVisible = ref(false)
const feedbackSourceDrawerData = ref()
const feedbackSourceDeleteData = ref()
const feedbackSourceDrawerType = ref('Add')

const showFeedbackSource = (feedbackSourceData, type) => {
  feedbackSourceDrawerData.value = { ...feedbackSourceData }
  feedbackSourceDrawerType.value = type
  isFeedbackSourceDrawerVisible.value = true
}

const deleteFeedbackSource = async (confirm, data) => {
  if(data?.id && confirm ){
    await feedbackSourceService.deleteFeedbackSource(data.id, () => {
      fetchFeedbackSources()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = feedbackSourceData => {
  feedbackSourceDeleteData.value = feedbackSourceData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    feedbackSourceDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <!-- 👉 Left side: Filters -->
        <div class="d-flex align-center flex-wrap gap-4 filter-section">
          <div class="filter-field filter-field--search">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
          <div class="filter-field">
            <AppSelect
              v-model="sourceType"
              :items="sourceTypes"
              :item-title="item => item.title"
              :item-value="item => item.value"
              placeholder="Filter By Source Type"
              clearable
            />
          </div>
        </div>

        <VSpacer />

        <!-- 👉 Right side: Action Buttons -->
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            v-if="$can('create', 'master.feedback_source')"
            prepend-icon="tabler-plus"
            variant="flat"
            @click="showFeedbackSource({}, 'Add')"
          >
            Add Source
          </VBtn>

          <IconBtn
            variant="tonal"
            class="rounded"
            :loading="isFetchingSources"
            @click="fetchFeedbackSources"
          >
            <VIcon
              size="24"
              icon="tabler-refresh"
            />
          </IconBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="feedbackSources"
        :items-length="totalFeedbackSources"
        :headers="headers"
        :loading="isFetchingSources"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <span
            class="text-base font-weight-medium cursor-pointer"
            @click="showFeedbackSource(item, 'View')"
          >
            {{ item.name }}
          </span>
        </template>

        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>

        <template #item.status="{ item }">
          <VChip
            size="x-small"
            :color="item.status ? 'primary' : 'secondary'"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.feedback_source')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'master.feedback_source')"
            @click="showFeedbackSource(item, 'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <FeedbackSourceDrawer
      v-model:isDrawerOpen="isFeedbackSourceDrawerVisible"
      :feedback-source-data="feedbackSourceDrawerData"
      :type="feedbackSourceDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="feedbackSourceDrawerType = $event"
      @update:feedback-source-data="updateFeedbackSource"
      @save-feedback-source="saveFeedbackSource"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure you want to delete this feedback source?"
      confirm-msg="Feedback source has been deleted successfully."
      confirm-title="Feedback Source Deleted!"
      :data="feedbackSourceDeleteData"
      @confirm="deleteFeedbackSource"
    />
  </section>
</template>

<style scoped>
.filter-field {
  inline-size: 15rem;
}

.filter-field--search {
  inline-size: 15rem;
}

@media (max-width: 600px) {
  .filter-section {
    inline-size: 100%;
  }

  .filter-field,
  .filter-field--search {
    inline-size: 100%;
  }
}

:deep(.v-data-table-footer) {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>
